import { useCallback, useMemo, useState } from 'react';

type FormData = Record<string, unknown>;

export const useFormWizard = <PageIdType, FinalType>(
  pages: ReadonlyArray<PageIdType>,
  onSecondaryAction: (data?: FormData) => void, // run when going 'back' on the first page
  onSubmit: (data: FinalType) => void // run when going 'forward' on the last page
) => {
  const [formState, setFormState] = useState<Array<FormData>>(
    Array.from({ length: pages.length }, () => ({}))
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const getFormState = useCallback(
    () => formState.reduce((acc, val) => ({ ...acc, ...val })),
    [formState]
  );

  const createNavigateAction = useCallback(
    (step: number) => (formData?: FormData) => {
      if (formData) {
        setFormState((oldValues) => {
          const newValues = [...oldValues];
          newValues[currentIndex] = formData;
          return newValues;
        });
      }
      setCurrentIndex((index) => index + step);
      window.scrollTo(0, 0);
    },
    [currentIndex]
  );

  const currentPage = useMemo(
    () => ({
      index: currentIndex,
      id: pages[currentIndex],
      values: formState[currentIndex],
      onSecondaryAction: currentIndex === 0 ? onSecondaryAction : createNavigateAction(-1),
      onFormSubmit: currentIndex === pages.length - 1 ? onSubmit : createNavigateAction(1),
    }),
    [currentIndex, formState, createNavigateAction, onSecondaryAction, onSubmit, pages]
  );

  return {
    currentPage,
    getFormState,
  };
};
